import {useCallback, useRef, useEffect, useMemo} from 'react';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {shallowEqual, useSelector} from 'react-redux';
import * as THREE from 'three';

import {
    LShapedDimension,
    RectangularDimension,
    CabinetMesh,
} from 'components/customer/RoomPlanner/types';
import {
    getScale,
    getRoomDimensions,
    getRoomType,
    getIsEditingRoomDimension,
    getHiddenWall,
    setRectangularElementDimension,
    getIsTopView,
    getIsEditHeightView,
    setLShapedElementDimension,
    getIsWireframeMode,
    getIsViewingAtBottom,
    getIsCreatingSnapshots,
    getPreviewPosition,
    getIsDragging,
    getOldCabinetsPosition,
    getPlaceholderIdsKey,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {
    getMeshSizeInPixels,
    addEdgeLines as addEdgeLinesHelper,
} from 'components/customer/RoomPlanner/helpers/mesh';
import {applyShadows} from 'components/customer/RoomPlanner/helpers/shadows';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {RoomPlannerFloorTexture as FLOOR_TEXTURE} from 'assets';

import {
    removeExistingMeasurements,
    setupMeasurements,
} from 'components/customer/RoomPlanner/helpers/measurement/setupMeasurements';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import {getItemsForWallMeasurements} from 'components/customer/RoomPlanner/helpers/measurement/measurementData';
import {
    setupSideButtons,
    setupDistanceLines,
    SideButtonGroups,
    disposeDistanceLines,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {
    cabinetReposition,
    repositionCabinetsOutsideRoom,
} from 'components/customer/RoomPlanner/helpers/cabinetPosition';
import {getFloorClearanceSceneUnits} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import {getCabinetLikeWorldOrigin} from 'components/customer/RoomPlanner/helpers/placeholder/getCabinetLikeWorldOrigin';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {
    setObjectVisibility,
    getArrowType,
    calculateArrowVisibility,
    disposeMovementArrowsOnDrag,
    followsWallVisibility,
} from 'components/customer/RoomPlanner/helpers/dragVisibilityHelpers';
import {
    addArrowsAndTextForCabinetDimension,
    createRoomDimensionArrow,
    addCabinetMovementArrows,
    addDistanceWallMeasurements,
    addDashedLines,
    syncOverlayTransformToCabinet,
} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {resetCabinetPosition} from 'components/customer/RoomPlanner/helpers/resetCabinetPosition';
import {
    isScene,
    syncOverlaysOnCabinetMove,
} from 'components/customer/RoomPlanner/helpers/syncOverlayOnCabinetMove';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';

const hideSideButtons = (groups: SideButtonGroups | null) => {
    if (!groups) return;
    groups.buttonGroup.visible = false;
    groups.leftButton.visible = false;
    groups.rightButton.visible = false;
};

// Print views whose measurement groups are all built once when report
// generation starts; the snapshot loop then only toggles their visibility.
const PRINT_MEASUREMENT_VIEWS: PreviewPosition[] = [
    'TOP',
    'FRONT',
    'RIGHT_LEVELED',
    'LEFT_LEVELED',
];

const getPrintLabelFontSize = (view: PreviewPosition): string => {
    if (view === 'TOP') {
        return 'bold 50px Arial';
    }
    if (view === 'LEFT_LEVELED' || view === 'RIGHT_LEVELED') {
        return 'bold 32px Arial';
    }
    return 'bold 40px Arial';
};

const useRoomLayout = (
    showArrows: boolean,
    dragPosition: THREE.Vector3, // Vector3 of the drag position in the scene
    cabinetGroup: THREE.Group<THREE.Object3DEventMap>,
    getGroupWidth: (group: THREE.Group<THREE.Object3DEventMap>) => {
        width: number;
        height: number;
        depth: number;
    },
    cabinetGroupListRef: React.MutableRefObject<
        THREE.Group<THREE.Object3DEventMap>[]
    >,
    showCabinets: boolean,
    rotating: boolean
) => {
    const dispatch = useAppDispatch();
    const scale = useAppSelector(getScale);
    const arrowRefs = useRef<(THREE.ArrowHelper | THREE.SpriteMaterial)[]>([]);
    const arrowGroupRefs = useRef<THREE.Group<THREE.Object3DEventMap>[]>([]);
    const topViewArrowRefs = useRef<THREE.Group<THREE.Object3DEventMap>[]>([]);
    const leftLeveledStaticArrowRef =
        useRef<THREE.Group<THREE.Object3DEventMap>>(null);
    const wallRefs = useRef<
        THREE.Mesh<
            THREE.ExtrudeGeometry,
            THREE.MeshStandardMaterial,
            THREE.Object3DEventMap
        >[]
    >([]);
    const floorRef =
        useRef<
            THREE.Mesh<
                THREE.ExtrudeGeometry,
                THREE.MeshStandardMaterial,
                THREE.Object3DEventMap
            >
        >();

    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const isMobileDevice = isMobileLandscape || isPortraitDevices;

    const leftArrowWidth = useRef<number>(0);
    const showArrowsRef = useRef<boolean>(showArrows);
    const sideButtonGroupsRef = useRef<SideButtonGroups | null>(null);
    const distanceLinesGroupRef = useRef<THREE.Group | null>(null);

    const {rectangularDimension, lShapedDimension} = useAppSelector(
        getRoomDimensions,
        shallowEqual
    );
    const roomType = useAppSelector(getRoomType);
    const hiddenWall = useAppSelector(getHiddenWall);
    const isTopView = useAppSelector(getIsTopView);
    const isEditHeightView = useAppSelector(getIsEditHeightView);
    const isWireframeMode = useAppSelector(getIsWireframeMode);
    const isWireframeModeRef = useRef<boolean>(isWireframeMode);
    const {selectedCabinetGroup, placeholderListRef} = useRoomPlannerContext();
    const placeholderIdsKey = useAppSelector(getPlaceholderIdsKey);
    const isRoomPreview = useRoomPreview();
    const isViewingAtBottom = useAppSelector(getIsViewingAtBottom);
    const isEditingRoomDimension = useAppSelector(getIsEditingRoomDimension);
    const isDragging = useAppSelector(getIsDragging); // Dragging boolean from the store
    // Track previous cabinet values to detect changes (works for all products)
    const prevCabinetValuesRef = useRef<string>('');
    // Helper function to serialize cabinet values for change detection
    const getCabinetValuesKey = useCallback(
        (cabinetGroup: THREE.Group | null): string => {
            if (!cabinetGroup?.userData?.values) return '';
            const values = cabinetGroup.userData.values as {
                cabinet_width?: number | string;
                cabinet_panel_width?: number | string;
                cabinet_height?: number | string;
                cabinet_panel_length?: number | string;
                cabinet_depth?: number | string;
                cabinet_ext_thickness?: number | string;
                cabinet_carc_thickness?: number | string;
                cabinet_left_width?: number | string;
                cabinet_right_width?: number | string;
                width2?: number | string;
                width?: number | string;
                cabinet_left_depth?: number | string;
                cabinet_right_depth?: number | string;
                cabinet_width_depth?: number | string;
                cabinet_length_depth?: number | string;
                width_depth?: number | string;
                length_depth?: number | string;
            };
            return JSON.stringify({
                width: values.cabinet_width || values.cabinet_panel_width,
                height: values.cabinet_height || values.cabinet_panel_length,
                depth: values.cabinet_depth,
                panelWidth: values.cabinet_panel_width,
                panelLength: values.cabinet_panel_length,
                extThickness: values.cabinet_ext_thickness,
                carcThickness: values.cabinet_carc_thickness,
                leftWidth: values.cabinet_left_width ?? values.width2,
                rightWidth: values.cabinet_right_width ?? values.width,
                leftDepth:
                    values.cabinet_left_depth ??
                    values.cabinet_width_depth ??
                    values.width_depth,
                rightDepth:
                    values.cabinet_right_depth ??
                    values.cabinet_length_depth ??
                    values.length_depth,
            });
        },
        []
    );

    const currentPreviewPosition = useAppSelector(getPreviewPosition);
    const previewPosition = useRef<string>(currentPreviewPosition);
    useEffect(() => {
        previewPosition.current = currentPreviewPosition;
    }, [currentPreviewPosition]);

    // Refs to track previous dimensions for cabinet repositioning
    const prevRectangularDimension = useRef(rectangularDimension);
    const prevLShapedDimension = useRef(lShapedDimension);
    const hasTriggeredRepositioning = useRef(false);

    const isRoomPlannerReportGenerating = useAppSelector(
        getIsCreatingSnapshots
    );
    const isReportGenerating = useRef<boolean>(isRoomPlannerReportGenerating);
    useEffect(() => {
        isReportGenerating.current = isRoomPlannerReportGenerating;
    }, [isRoomPlannerReportGenerating]);

    // Whether the per-view print measurement groups have been built for the
    // current report-generation session
    const printMeasurementsBuiltRef = useRef(false);

    const defaultLabelFontSize = useMemo(() => {
        if (isRoomPlannerReportGenerating) {
            if (
                currentPreviewPosition === 'TOP' ||
                currentPreviewPosition === 'TOP_WITHOUT_EDITING'
            ) {
                return 'bold 50px Arial';
            } else if (
                ['LEFT_LEVELED', 'RIGHT_LEVELED'].includes(
                    currentPreviewPosition
                )
            ) {
                return 'bold 32px Arial';
            }

            return 'bold 40px Arial';
        }
        return 'bold 27px Arial';
    }, [isRoomPlannerReportGenerating, currentPreviewPosition]);

    const handleArrowsAndGroup = (
        arrows: (
            THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Line | THREE.Mesh
        )[],
        arrowGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => {
        // First, clean up any disposed arrows from our tracking
        arrowRefs.current = arrowRefs.current.filter(
            (arrow) => !arrow.userData?.isDisposed
        );

        // Clean up any disposed arrow groups
        if (arrowGroupRefs) {
            arrowGroupRefs.current = arrowGroupRefs.current.filter(
                (group) => group && !group.userData?.isDisposed
            );
        }

        // Now add the new arrows if they're not already tracked
        arrows.forEach((arrow) => {
            if (
                !arrowRefs.current.includes(
                    arrow as THREE.ArrowHelper | THREE.SpriteMaterial
                ) &&
                !arrow.userData?.isDisposed
            ) {
                arrowRefs.current.push(
                    arrow as THREE.ArrowHelper | THREE.SpriteMaterial
                );

                // Make sure all arrows respond to showArrows flag changes
                if (arrow.userData) {
                    arrow.userData.isVisibilityControlled = true;
                } else {
                    arrow.userData = {isVisibilityControlled: true};
                }
            }
        });

        if (
            arrowGroup &&
            arrowGroupRefs &&
            !arrowGroupRefs.current.includes(arrowGroup) &&
            !arrowGroup.userData?.isDisposed
        ) {
            arrowGroupRefs.current.push(arrowGroup);
        }
    };

    const createLShapedWalls = useCallback(
        (
            scene: THREE.Scene,
            dimension: LShapedDimension,
            camera: THREE.Camera,
            renderer: React.MutableRefObject<THREE.WebGLRenderer | null>,
            isLeftLeveledStaticView: boolean
        ) => {
            const leftWidth = dimension.leftWidth * scale - 25;
            const rightWidth = dimension.rightWidth * scale + 50;
            const rightDepth = dimension.rightDepth * scale - 25;
            const leftDepth = dimension.leftDepth * scale + 60;
            const wallHeight = dimension.height * scale + 0.4;
            const positionY = -(wallHeight / 2) + 50.4;

            const rightWidthWallMaterial = new THREE.MeshStandardMaterial({
                color: '#fff',
            });

            rightWidthWallMaterial.transparent = true;
            rightWidthWallMaterial.opacity = 1;

            const leftWidthWallMaterial = rightWidthWallMaterial.clone();
            const rightDepthWallMaterial = rightWidthWallMaterial.clone();
            const leftDepthWallMaterial = rightWidthWallMaterial.clone();
            const rightWidth2WallMaterial = rightWidthWallMaterial.clone();
            const leftWidth2WallMaterial = rightWidthWallMaterial.clone();

            const rightWidthShape = new THREE.Shape();
            rightWidthShape.moveTo(0, 0);
            rightWidthShape.lineTo(rightWidth, 0);
            rightWidthShape.lineTo(rightWidth, wallHeight);
            rightWidthShape.lineTo(0, wallHeight);
            rightWidthShape.lineTo(0, 0);

            const leftDepthShape = new THREE.Shape();
            leftDepthShape.moveTo(0, 0);
            leftDepthShape.lineTo(leftDepth, 0);
            leftDepthShape.lineTo(leftDepth, wallHeight);
            leftDepthShape.lineTo(0, wallHeight);
            leftDepthShape.lineTo(0, 0);

            const leftWidthShape = new THREE.Shape();
            leftWidthShape.moveTo(0, 0);
            leftWidthShape.lineTo(leftWidth + 25, 0);
            leftWidthShape.lineTo(leftWidth + 25, wallHeight);
            leftWidthShape.lineTo(0, wallHeight);
            leftWidthShape.lineTo(0, 0);

            const rightDepthWallShape = new THREE.Shape();
            rightDepthWallShape.moveTo(0, 0);
            rightDepthWallShape.lineTo(rightDepth + 25, 0);
            rightDepthWallShape.lineTo(rightDepth + 25, wallHeight);
            rightDepthWallShape.lineTo(0, wallHeight);
            rightDepthWallShape.lineTo(0, 0);

            const rightWidth2Shape = new THREE.Shape();
            rightWidth2Shape.moveTo(0, 0);
            rightWidth2Shape.lineTo(rightWidth - leftDepth, 0);
            rightWidth2Shape.lineTo(rightWidth - leftDepth, wallHeight);
            rightWidth2Shape.lineTo(0, wallHeight);
            rightWidth2Shape.lineTo(0, 0);

            const leftWidth2Shape = new THREE.Shape();
            leftWidth2Shape.moveTo(0, 0);
            leftWidth2Shape.lineTo(leftWidth - rightDepth, 0);
            leftWidth2Shape.lineTo(leftWidth - rightDepth, wallHeight);
            leftWidth2Shape.lineTo(0, wallHeight);
            leftWidth2Shape.lineTo(0, 0);

            // Extrude Settings
            const extrudeSettings = {
                depth: 25,
                bevelEnabled: false,
            };

            const rightWidthGeometry = new THREE.ExtrudeGeometry(
                rightWidthShape,
                extrudeSettings
            );

            const leftDepthGeometry = new THREE.ExtrudeGeometry(
                leftDepthShape,
                extrudeSettings
            );

            const leftWidthGeometry = new THREE.ExtrudeGeometry(
                leftWidthShape,
                extrudeSettings
            );

            const rightDepthWallGeometry = new THREE.ExtrudeGeometry(
                rightDepthWallShape,
                extrudeSettings
            );

            const rightWidth2Geometry = new THREE.ExtrudeGeometry(
                rightWidth2Shape,
                extrudeSettings
            );

            const leftWidth2Geometry = new THREE.ExtrudeGeometry(
                leftWidth2Shape,
                extrudeSettings
            );

            const rightWidthWall = new THREE.Mesh(
                rightWidthGeometry,
                rightWidthWallMaterial
            );
            rightWidthWall.userData.id = 'rightWidthWall';
            rightWidthWall.position.set(
                rightWidth / 2,
                positionY,
                -(leftWidth / 2 + 25)
            );
            rightWidthWall.rotation.y = Math.PI;

            if (!isDragging) {
                addArrowsAndTextForCabinetDimension(
                    rightWidthWall,
                    leftArrowWidth.current,
                    handleArrowsAndGroup,
                    false,
                    false,
                    false,
                    'arrowGroupBack'
                );
            }

            const leftDepthWall = new THREE.Mesh(
                leftDepthGeometry,
                leftDepthWallMaterial
            );
            leftDepthWall.userData.id = 'leftDepthWall';

            leftDepthWall.position.set(
                -rightWidth / 2,
                positionY,
                leftWidth / 2
            );

            if (!isDragging) {
                addArrowsAndTextForCabinetDimension(
                    leftDepthWall,
                    leftArrowWidth.current,
                    handleArrowsAndGroup,
                    false,
                    false,
                    false,
                    'arrowGroupFront'
                );
            }

            const leftWidthWall = new THREE.Mesh(
                leftWidthGeometry,
                leftWidthWallMaterial
            );
            leftWidthWall.userData.id = 'leftWidthWall';
            leftWidthWall.position.set(
                -rightWidth / 2,
                positionY,
                leftWidth / 2
            );
            leftWidthWall.rotation.y = Math.PI / 2;

            if (!isDragging) {
                addArrowsAndTextForCabinetDimension(
                    leftWidthWall,
                    leftArrowWidth.current,
                    handleArrowsAndGroup,
                    true,
                    false,
                    false,
                    'arrowGroupLeft-distance',
                    scale
                );
            }

            const rightDepthWall = new THREE.Mesh(
                rightDepthWallGeometry,
                rightDepthWallMaterial
            );
            rightDepthWall.userData.id = 'rightDepthWall';
            rightDepthWall.position.set(
                rightWidth / 2,
                positionY,
                -(leftWidth / 2 + 25)
            );
            rightDepthWall.rotation.y = -Math.PI / 2;

            if (!isDragging) {
                addArrowsAndTextForCabinetDimension(
                    rightDepthWall,
                    leftArrowWidth.current,
                    handleArrowsAndGroup,
                    true,
                    false,
                    false,
                    'arrowGroupRight'
                );
            }

            const rightWidth2Wall = new THREE.Mesh(
                rightWidth2Geometry,
                rightWidth2WallMaterial
            );
            rightWidth2Wall.userData.id = 'rightWidth2Wall';
            rightWidth2Wall.position.set(
                rightWidth - leftDepth - (rightWidth / 2 - leftDepth),
                positionY,
                -(leftWidth / 2 - rightDepth) + 25
            );
            rightWidth2Wall.rotation.y = Math.PI;

            const leftWidth2Wall = new THREE.Mesh(
                leftWidth2Geometry,
                leftWidth2WallMaterial
            );
            leftWidth2Wall.userData.id = 'leftWidth2Wall';
            leftWidth2Wall.position.set(
                leftDepth - rightWidth / 2,
                positionY,
                -(leftWidth / 2 - rightDepth)
            );
            leftWidth2Wall.rotation.y = -Math.PI / 2;

            const wallGroup = new THREE.Group();
            wallGroup.add(leftWidthWall);
            wallGroup.add(leftWidth2Wall);
            wallGroup.add(rightWidthWall);
            wallGroup.add(rightWidth2Wall);
            wallGroup.add(leftDepthWall);
            wallGroup.add(rightDepthWall);

            wallGroup.position.x = 35;
            wallGroup.name = ObjectNameMap.lShapeWall;
            // Hidden walls are skipped by the shadow pass (onHide moves them to
            // layer 1 and sets visible = false), so only visible walls cast.
            applyShadows(wallGroup);
            scene.add(wallGroup);

            wallRefs.current.push(leftWidthWall);
            wallRefs.current.push(leftWidth2Wall);
            wallRefs.current.push(rightWidthWall);
            wallRefs.current.push(rightWidth2Wall);
            wallRefs.current.push(leftDepthWall);
            wallRefs.current.push(rightDepthWall);

            const edgeMaterial = new THREE.LineBasicMaterial({
                color: '#bdc3c7',
            });

            // Create edge lines for each wall
            const addEdgeLines = (mesh: THREE.Mesh, name: string) => {
                const edges = new THREE.EdgesGeometry(mesh.geometry);
                const line = new THREE.LineSegments(edges, edgeMaterial);
                line.name = name;
                mesh.add(line);
            };

            addEdgeLines(leftWidthWall, ObjectNameMap.leftWidthWall);
            addEdgeLines(leftWidth2Wall, ObjectNameMap.leftWidth2Wall);
            addEdgeLines(rightWidthWall, ObjectNameMap.rightWidthWall);
            addEdgeLines(rightWidth2Wall, ObjectNameMap.rightWidth2Wall);
            addEdgeLines(leftDepthWall, ObjectNameMap.leftDepthWall);
            addEdgeLines(rightDepthWall, ObjectNameMap.rightDepthWall);

            leftWidthWall.name = 'leftWidthWall';
            leftWidth2Wall.name = 'leftWidth2Wall';
            rightWidthWall.name = 'rightWidthWall';
            rightWidth2Wall.name = 'rightWidth2Wall';
            leftDepthWall.name = 'leftDepthWall';
            rightDepthWall.name = 'rightDepthWall';

            const elementWidthInPixels = getMeshSizeInPixels(
                rightWidthWall,
                camera,
                renderer.current
            );

            const leftWidthWallClone = leftWidthWall.clone();
            leftWidthWallClone.rotation.y = Math.PI;

            const elementLengthInPixels = getMeshSizeInPixels(
                leftWidthWallClone,
                camera,
                renderer.current
            );

            const elementLeftDepthInPixels = getMeshSizeInPixels(
                leftDepthWall,
                camera,
                renderer.current
            );

            const rightDepthWallClone = rightDepthWall.clone();
            rightDepthWallClone.rotation.y = Math.PI;

            const elementRightDepthInPixels = getMeshSizeInPixels(
                rightDepthWallClone,
                camera,
                renderer.current
            );

            dispatch(
                setLShapedElementDimension({
                    rightWidth: elementWidthInPixels.width,
                    leftWidth: elementLengthInPixels.width,
                    rightDepth: elementRightDepthInPixels.width,
                    leftDepth: elementLeftDepthInPixels.width,
                    height: elementWidthInPixels.height,
                })
            );

            const animationSpeed = 200;

            if (isLeftLeveledStaticView) {
                leftWidthWallMaterial.opacity = 0;
            } else {
                leftWidthWallMaterial.opacity = 1;
            }

            handleWallTransition(
                leftWidthWall,
                leftWidthWallMaterial,
                animationSpeed,
                arrowRefs.current.filter(
                    (arrow) => arrow.userData.id === 'leftWidthWall'
                ),
                edgeMaterial
            );
            handleWallTransition(
                leftWidth2Wall,
                leftWidth2WallMaterial,
                animationSpeed,
                arrowRefs.current.filter(
                    (arrow) => arrow.userData.id === 'leftWidth2Wall'
                ),
                edgeMaterial
            );
            handleWallTransition(
                rightWidthWall,
                rightWidthWallMaterial,
                animationSpeed,
                arrowRefs.current.filter(
                    (arrow) => arrow.userData.id === 'rightWidthWall'
                ),
                edgeMaterial
            );
            handleWallTransition(
                rightWidth2Wall,
                rightWidth2WallMaterial,
                animationSpeed,
                arrowRefs.current.filter(
                    (arrow) => arrow.userData.id === 'rightWidth2Wall'
                ),
                edgeMaterial
            );
            handleWallTransition(
                leftDepthWall,
                leftDepthWallMaterial,
                animationSpeed,
                arrowRefs.current.filter(
                    (arrow) => arrow.userData.id === 'leftDepthWall'
                ),
                edgeMaterial
            );
            handleWallTransition(
                rightDepthWall,
                rightDepthWallMaterial,
                animationSpeed,
                arrowRefs.current.filter(
                    (arrow) => arrow.userData.id === 'rightDepthWall'
                ),
                edgeMaterial
            );
        },
        [scale]
    );

    const createLShapedFloor = (
        scene: THREE.Scene,
        textureLoader: THREE.TextureLoader,
        dimension: LShapedDimension,
        isTopView: boolean,
        isLeftLeveledStaticView: boolean
    ) => {
        const leftWidth = dimension.leftWidth * scale - 25;
        const rightWidth = dimension.rightWidth * scale + 50;
        const rightDepth = dimension.rightDepth * scale - 25;
        const leftDepth = dimension.leftDepth * scale + 60;
        const wallHeight = dimension.height * scale + 20;

        const positionY = -(wallHeight / 2) + 60;

        const floorTexture = textureLoader.load(FLOOR_TEXTURE);
        floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
        floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
        floorTexture.offset.set(0, 0.5);
        floorTexture.repeat.set(0.001, 0.001);

        const floorMaterial = new THREE.MeshStandardMaterial({
            map: floorTexture,
            transparent: true,
            opacity: 1,
        });

        const floorShape = new THREE.Shape();
        floorShape.moveTo(0, 0);
        floorShape.lineTo(leftDepth - 50, 0);
        floorShape.lineTo(leftDepth - 50, leftWidth - rightDepth);
        floorShape.lineTo(rightWidth - 50, leftWidth - rightDepth);
        floorShape.lineTo(rightWidth - 50, leftWidth + 25);
        floorShape.lineTo(0, leftWidth + 25);
        floorShape.lineTo(0, 0);
        floorShape.closePath();

        const extrudeSettings = {
            depth: 1,
            bevelEnabled: false,
        };

        const floorGeometry = new THREE.ExtrudeGeometry(
            floorShape,
            extrudeSettings
        );
        const floor = new THREE.Mesh(floorGeometry, floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = positionY;
        floor.position.z = leftWidth / 2;
        floor.position.x = -(rightWidth / 2) + 60;

        // Add arrows for top and left-leveled static views

        const topArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(1, 0, 0),
            reverseArrowDir: new THREE.Vector3(-1, 0, 0),
            arrowOrigin: new THREE.Vector3(
                -(rightWidth / 2) + 60,
                positionY + wallHeight,
                -leftWidth / 2 - 15
            ),
            arrowLength: rightWidth - 50,
            onArrowsCreated: (arrow, reverseArrow) => {
                arrow.rotation.x = -Math.PI / 2;
                reverseArrow.rotation.x = -Math.PI / 2;
            },
        });

        const leftArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(1, 0, 0),
            reverseArrowDir: new THREE.Vector3(-1, 0, 0),
            arrowOrigin: new THREE.Vector3(
                -(leftWidth / 2) + 10,
                positionY + wallHeight,
                -rightWidth / 2 + 32
            ),
            arrowLength: leftWidth,
            onArrowsCreated: (arrow, reverseArrow) => {
                arrow.rotation.x = -Math.PI / 2;
                reverseArrow.rotation.x = -Math.PI / 2;
            },
        });

        leftArrowGroup.rotation.y = Math.PI / 2;
        leftArrowGroup.position.x = 35;

        const rightDepthArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(1, 0, 0),
            reverseArrowDir: new THREE.Vector3(-1, 0, 0),
            arrowOrigin: new THREE.Vector3(
                -(leftWidth / 2) + leftWidth - rightDepth + 10,
                positionY + wallHeight,
                rightWidth / 2 - 32
            ),
            arrowLength: rightDepth,
            onArrowsCreated: (arrow, reverseArrow) => {
                arrow.rotation.x = -Math.PI / 2;
                reverseArrow.rotation.x = -Math.PI / 2;
            },
        });

        rightDepthArrowGroup.rotation.y = Math.PI / 2;
        rightDepthArrowGroup.position.x = 35;

        const leftDepthArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(1, 0, 0),
            reverseArrowDir: new THREE.Vector3(-1, 0, 0),
            arrowOrigin: new THREE.Vector3(
                -(rightWidth / 2) + 67,
                positionY + wallHeight,
                leftWidth / 2 - 10
            ),
            arrowLength: leftDepth - 60,
            onArrowsCreated: (arrow, reverseArrow) => {
                arrow.rotation.x = -Math.PI / 2;
                reverseArrow.rotation.x = -Math.PI / 2;
            },
        });

        scene.add(topArrowGroup);
        scene.add(leftArrowGroup);
        scene.add(rightDepthArrowGroup);
        scene.add(leftDepthArrowGroup);
        topArrowGroup.visible = isTopView;
        leftArrowGroup.visible = isTopView;
        rightDepthArrowGroup.visible = isTopView;
        leftDepthArrowGroup.visible = isTopView;
        topViewArrowRefs.current.push(topArrowGroup);
        topViewArrowRefs.current.push(leftArrowGroup);
        topViewArrowRefs.current.push(rightDepthArrowGroup);
        topViewArrowRefs.current.push(leftDepthArrowGroup);

        const verticalArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(0, 1, 0),
            reverseArrowDir: new THREE.Vector3(0, -1, 0),
            arrowOrigin: new THREE.Vector3(
                leftWidth / 2 + 15,
                positionY,
                -rightWidth / 2
            ),
            arrowLength: wallHeight,
            isVertical: true,
        });

        verticalArrowGroup.rotation.y = Math.PI / 2;
        scene.add(verticalArrowGroup);
        verticalArrowGroup.visible = isLeftLeveledStaticView;
        leftLeveledStaticArrowRef.current = verticalArrowGroup;

        const {edgeMaterial} = addEdgeLinesHelper(floor, ObjectNameMap.floor);
        floor.userData.onToggleWireframe = (isWireframe: boolean) => {
            edgeMaterial.opacity = isWireframe ? 1 : 0;
            floorMaterial.opacity = isWireframe ? 0 : 1;
        };

        floorRef.current = floor;

        floor.name = ObjectNameMap.lshapeFloor;
        applyShadows(floor, {cast: false});
        scene.add(floor);
    };

    const createRectangularFloor = (
        scene: THREE.Scene,
        textureLoader: THREE.TextureLoader,
        dimension: RectangularDimension,
        isTopView: boolean,
        isLeftLeveledStaticView: boolean
    ) => {
        const length = dimension.length * scale - 25;
        const width = dimension.width * scale + 50;
        const wallHeight = dimension.height * scale;
        const positionY = -(wallHeight / 2) + 50;

        const floorTexture = textureLoader.load(FLOOR_TEXTURE);
        floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
        floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
        floorTexture.offset.set(0, 0.5);
        floorTexture.repeat.set(0.001, 0.001);

        const floorMaterial = new THREE.MeshStandardMaterial({
            map: floorTexture,
            transparent: true,
            opacity: 1,
        });

        const floorShape = new THREE.Shape();
        floorShape.moveTo(0, 0);
        floorShape.lineTo(0, length + 25);
        floorShape.lineTo(width - 50, length + 25);
        floorShape.lineTo(width - 50, 0);
        floorShape.closePath();

        const extrudeSettings = {
            depth: 1,
            bevelEnabled: false,
        };

        const floorGeometry = new THREE.ExtrudeGeometry(
            floorShape,
            extrudeSettings
        );
        const floor = new THREE.Mesh(floorGeometry, floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = positionY;
        floor.position.x = -(width / 2) + 60;
        floor.position.z = length / 2;

        // Add arrows for top and left-leveled static views

        const topArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(1, 0, 0),
            reverseArrowDir: new THREE.Vector3(-1, 0, 0),
            arrowOrigin: new THREE.Vector3(
                -(width / 2) + 60,
                positionY + wallHeight,
                -length / 2 - 15
            ),
            arrowLength: width - 50,
            onArrowsCreated: (arrow, reverseArrow) => {
                arrow.rotation.x = -Math.PI / 2;
                reverseArrow.rotation.x = -Math.PI / 2;
            },
        });

        const leftArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(1, 0, 0),
            reverseArrowDir: new THREE.Vector3(-1, 0, 0),
            arrowOrigin: new THREE.Vector3(
                -(length / 2) + 10,
                positionY + wallHeight,
                -width / 2 + 32
            ),
            arrowLength: length,
            onArrowsCreated: (arrow, reverseArrow) => {
                arrow.rotation.x = -Math.PI / 2;
                reverseArrow.rotation.x = -Math.PI / 2;
            },
        });

        leftArrowGroup.rotation.y = Math.PI / 2;
        leftArrowGroup.position.x = 35;

        scene.add(topArrowGroup);
        scene.add(leftArrowGroup);
        topArrowGroup.visible = isTopView;
        leftArrowGroup.visible = isTopView;
        topViewArrowRefs.current.push(topArrowGroup);
        topViewArrowRefs.current.push(leftArrowGroup);

        const verticalArrowGroup = createRoomDimensionArrow({
            arrowDir: new THREE.Vector3(0, 1, 0),
            reverseArrowDir: new THREE.Vector3(0, -1, 0),
            arrowOrigin: new THREE.Vector3(
                length / 2 + 15,
                positionY,
                -width / 2
            ),
            arrowLength: wallHeight + 20,
            isVertical: true,
        });

        verticalArrowGroup.rotation.y = Math.PI / 2;
        scene.add(verticalArrowGroup);
        verticalArrowGroup.visible = isLeftLeveledStaticView;
        leftLeveledStaticArrowRef.current = verticalArrowGroup;

        floor.name = ObjectNameMap.floor;
        const {edgeMaterial} = addEdgeLinesHelper(floor, ObjectNameMap.floor);
        applyShadows(floor, {cast: false});
        scene.add(floor);

        floor.userData.onToggleWireframe = (isWireframe: boolean) => {
            edgeMaterial.opacity = isWireframe ? 1 : 0;
            floorMaterial.opacity = isWireframe ? 0 : 1;
        };

        floorRef.current = floor;
    };

    const createRectangularWalls = (
        scene: THREE.Scene,
        dimension: RectangularDimension,
        camera: THREE.Camera,
        renderer: React.MutableRefObject<THREE.WebGLRenderer | null>,
        isLeftLeveledStaticView: boolean
    ) => {
        const length = dimension.length * scale - 25;
        const width = dimension.width * scale + 50;
        const wallHeight = dimension.height * scale + 0.4;
        const positionY = -(wallHeight / 2) + 50.4;

        const backWallMaterial = new THREE.MeshStandardMaterial({
            color: '#fff',
        });

        backWallMaterial.transparent = true;
        backWallMaterial.opacity = 1;

        const frontWallMaterial = backWallMaterial.clone();
        const leftWallMaterial = backWallMaterial.clone();
        const rightWallMaterial = backWallMaterial.clone();

        const wallShape = new THREE.Shape();
        wallShape.moveTo(0, 0);
        wallShape.lineTo(width - 25, 0);
        wallShape.lineTo(width - 25, wallHeight);
        wallShape.lineTo(0, wallHeight);
        wallShape.lineTo(0, 0);

        const leftWallShape = new THREE.Shape();
        leftWallShape.moveTo(0, 0);
        leftWallShape.lineTo(length + 25, 0);
        leftWallShape.lineTo(length + 25, wallHeight);
        leftWallShape.lineTo(0, wallHeight);
        leftWallShape.lineTo(0, 0);

        const rightWallShape = new THREE.Shape();
        rightWallShape.moveTo(0, 0);
        rightWallShape.lineTo(length + 75, 0);
        rightWallShape.lineTo(length + 75, wallHeight);
        rightWallShape.lineTo(0, wallHeight);
        rightWallShape.lineTo(0, 0);

        const extrudeSettings = {
            depth: 25,
            bevelEnabled: false,
        };

        const wallGeometry = new THREE.ExtrudeGeometry(
            wallShape,
            extrudeSettings
        );

        const leftWallGeometry = new THREE.ExtrudeGeometry(
            leftWallShape,
            extrudeSettings
        );

        const rightWallGeometry = new THREE.ExtrudeGeometry(
            rightWallShape,
            extrudeSettings
        );

        const backWall = new THREE.Mesh(wallGeometry, backWallMaterial);
        backWall.position.set(width / 2 - 25, positionY, -length / 2 - 25);
        backWall.rotation.y = Math.PI;
        backWall.userData.id = 'backWall';
        if (!isDragging) {
            addArrowsAndTextForCabinetDimension(
                backWall,
                leftArrowWidth.current,
                handleArrowsAndGroup,
                false,
                false,
                false,
                'arrowGroupBack'
            );
        }

        const frontWall = new THREE.Mesh(wallGeometry, frontWallMaterial);
        frontWall.position.set(width / 2 - 25, positionY, length / 2 + 25);
        frontWall.rotation.y = Math.PI;
        frontWall.userData.id = 'frontWall';

        if (!isDragging) {
            addArrowsAndTextForCabinetDimension(
                frontWall,
                leftArrowWidth.current,
                handleArrowsAndGroup,
                false,
                false,
                false,
                'arrowGroupFront'
            );
        }

        const leftWall = new THREE.Mesh(leftWallGeometry, leftWallMaterial);
        leftWall.position.set(-width / 2, positionY, length / 2);
        leftWall.rotation.y = Math.PI / 2;
        leftWall.userData.id = 'leftWall';

        if (!isDragging) {
            // addArrowsAndTextForCabinetDimension(
            //     leftWall,
            //     leftArrowWidth.current,
            //     handleArrowsAndGroup,
            //     true,
            //     false,
            //     false,
            //     'arrowGroupLeft'
            // );

            addArrowsAndTextForCabinetDimension(
                leftWall,
                leftArrowWidth.current,
                handleArrowsAndGroup,
                true,
                false,
                false,
                'arrowGroupLeft-distance',
                scale
            );
        }

        const rightWall = new THREE.Mesh(rightWallGeometry, rightWallMaterial);
        rightWall.position.set(width / 2 - 25, positionY, length / 2 + 25);
        rightWall.rotation.y = Math.PI / 2;
        rightWall.userData.id = 'rightWall';

        if (!isDragging) {
            addArrowsAndTextForCabinetDimension(
                rightWall,
                leftArrowWidth.current,
                handleArrowsAndGroup,
                true,
                false,
                false,
                'arrowGroupRight'
            );
        }

        const edgeMaterial = new THREE.LineBasicMaterial({color: '#bdc3c7'});

        // Create edge lines for each wall
        const addEdgeLines = (mesh: THREE.Mesh, name: string) => {
            const edges = new THREE.EdgesGeometry(mesh.geometry);
            const line = new THREE.LineSegments(edges, edgeMaterial);
            line.name = name;
            mesh.add(line);
        };

        addEdgeLines(backWall, ObjectNameMap.wallEdgeBack);
        addEdgeLines(frontWall, ObjectNameMap.wallEdgeFront);
        addEdgeLines(leftWall, ObjectNameMap.wallEdgeLeft);
        addEdgeLines(rightWall, ObjectNameMap.wallEdgeRight);

        backWall.name = 'wall:back';
        frontWall.name = 'wall:front';
        leftWall.name = 'wall:left';
        rightWall.name = 'wall:right';

        wallRefs.current.push(leftWall);
        wallRefs.current.push(rightWall);
        wallRefs.current.push(backWall);
        wallRefs.current.push(frontWall);

        const elementWidthInPixels = getMeshSizeInPixels(
            backWall,
            camera,
            renderer.current
        );

        const leftWallClone = leftWall.clone();
        leftWallClone.rotation.y = Math.PI;

        const elementLengthInPixels = getMeshSizeInPixels(
            leftWallClone,
            camera,
            renderer.current
        );

        dispatch(
            setRectangularElementDimension({
                width: elementWidthInPixels.width,
                length: elementLengthInPixels.width,
                height: elementWidthInPixels.height,
            })
        );

        if (isLeftLeveledStaticView) {
            leftWallMaterial.opacity = 0;
            rightWallMaterial.opacity = 1;
        } else {
            frontWallMaterial.opacity = 0;
            rightWallMaterial.opacity = 0;
        }

        const animationSpeed = 200;

        const wallGroup = new THREE.Group();
        wallGroup.add(backWall);
        wallGroup.add(frontWall);
        wallGroup.add(leftWall);
        wallGroup.add(rightWall);

        wallGroup.position.x = 35;
        wallGroup.name = ObjectNameMap.rectangularWall;
        // Hidden walls are skipped by the shadow pass (onHide moves them to
        // layer 1 and sets visible = false), so only visible walls cast.
        applyShadows(wallGroup);
        scene.add(wallGroup);

        handleWallTransition(
            backWall,
            backWallMaterial,
            animationSpeed,
            arrowRefs.current.filter(
                (arrow) => arrow.userData.id === 'backWall'
            ),
            edgeMaterial
        );
        handleWallTransition(
            frontWall,
            frontWallMaterial,
            animationSpeed,
            arrowRefs.current.filter(
                (arrow) => arrow.userData.id === 'frontWall'
            ),
            edgeMaterial
        );
        handleWallTransition(
            leftWall,
            leftWallMaterial,
            animationSpeed,
            arrowRefs.current.filter(
                (arrow) => arrow.userData.id === 'leftWall'
            ),
            edgeMaterial
        );
        handleWallTransition(
            rightWall,
            rightWallMaterial,
            animationSpeed,
            arrowRefs.current.filter(
                (arrow) => arrow.userData.id === 'rightWall'
            ),
            edgeMaterial
        );
    };

    const isNameMatchToWall = (name: string) => {
        return (
            name === ObjectNameMap.wallEdgeBack ||
            name === ObjectNameMap.wallEdgeFront ||
            name === ObjectNameMap.wallEdgeLeft ||
            name === ObjectNameMap.wallEdgeRight ||
            name === ObjectNameMap.leftWidthWall ||
            name === ObjectNameMap.leftWidth2Wall ||
            name === ObjectNameMap.rightWidthWall ||
            name === ObjectNameMap.rightWidth2Wall ||
            name === ObjectNameMap.leftDepthWall ||
            name === ObjectNameMap.rightDepthWall
        );
    };

    const handleWallTransition = (
        wall: THREE.Mesh<
            THREE.ExtrudeGeometry,
            THREE.MeshStandardMaterial,
            THREE.Object3DEventMap
        >,
        material: THREE.MeshStandardMaterial,
        animationSpeed: number,
        arrows?: (THREE.ArrowHelper | THREE.SpriteMaterial)[],
        lineMaterial?: THREE.LineBasicMaterial
    ) => {
        wall.userData.onToggleWireframe = (isWireframe: boolean) => {
            wall.traverse((ch) => {
                if (isNameMatchToWall(ch.name)) {
                    material.opacity = isWireframe ? 0 : ch.visible ? 1 : 0;
                }
            });

            if (lineMaterial) {
                lineMaterial.color.set(isWireframe ? '#000' : '#bdc3c7');
            }
        };

        wall.layers.set(0);

        wall.userData.onHide = () => {
            wall.layers.set(1);
            new TWEEN.Tween(material, USE_LEGACY_TWEEN_GROUP)
                .to({opacity: 0}, animationSpeed)
                .easing(TWEEN.Easing.Cubic.Out)
                .onUpdate(() => {
                    wall.traverse((ch) => {
                        if (
                            isNameMatchToWall(ch.name) &&
                            !(
                                isReportGenerating.current &&
                                previewPosition.current === 'TOP' // don't hide walls on plan view
                            )
                        ) {
                            ch.visible = false;
                        }
                    });
                })
                .start();
            // Hide measurement groups when wall is hidden
            // Don't hide measurements when printing or in TOP views
            const shouldKeepMeasurementsVisible =
                isReportGenerating.current ||
                previewPosition.current === 'TOP' ||
                previewPosition.current === 'TOP_WITHOUT_EDITING';
            if (!shouldKeepMeasurementsVisible) {
                wall.traverse((child) => {
                    if (
                        child.userData.type === 'multipleMeasurements' ||
                        child.userData.type === 'emptySpaceMeasurements'
                    ) {
                        child.visible = false;
                    }
                });
                arrows?.forEach((arrow) => {
                    if (showArrowsRef.current) {
                        arrow.visible = false;
                    }
                });
            }
        };

        wall.userData.onShow = () => {
            const isWireframe = isWireframeModeRef.current;
            wall.layers.set(0);
            new TWEEN.Tween(material, USE_LEGACY_TWEEN_GROUP)
                .to({opacity: isWireframe ? 0 : 1}, animationSpeed)
                .easing(TWEEN.Easing.Cubic.Out)
                .onUpdate(() => {
                    wall.traverse((ch) => {
                        if (isNameMatchToWall(ch.name)) {
                            ch.visible = true;
                        }
                    });
                })
                .start();
            // Show measurement groups when wall is shown. During report
            // generation the snapshot loop owns measurement visibility.
            if (!isReportGenerating.current) {
                wall.traverse((child) => {
                    if (
                        child.userData.type === 'multipleMeasurements' ||
                        child.userData.type === 'emptySpaceMeasurements'
                    ) {
                        child.visible = true;
                    }
                });

                arrows?.forEach((arrow) => {
                    if (showArrowsRef.current) {
                        arrow.visible = true;
                    }
                });
            }
        };
    };

    useEffect(() => {
        showArrowsRef.current = showArrows;

        if (arrowRefs.current && roomType) {
            arrowRefs.current.forEach((arrow) => {
                // Reposition arrows are in this list too. Wall fade must not
                // turn them off; movement mode puts them back only on toggle.
                if (
                    arrow instanceof THREE.Object3D &&
                    !followsWallVisibility(arrow)
                ) {
                    return;
                }

                arrow.visible = showArrows;

                if (showArrows) {
                    let hiddenWalls: string[] = [];

                    if (roomType === 'RECTANGULAR') {
                        switch (hiddenWall) {
                            case 'backWall':
                                hiddenWalls = ['backWall', 'leftWall'];
                                break;
                            case 'frontWall':
                                hiddenWalls = ['frontWall', 'rightWall'];
                                break;
                            case 'leftWall':
                                hiddenWalls = ['leftWall', 'frontWall'];
                                break;
                            default:
                                hiddenWalls = ['rightWall', 'backWall'];
                                break;
                        }
                    }

                    if (roomType === 'LSHAPED') {
                        switch (hiddenWall) {
                            case 'leftWall':
                                hiddenWalls = [
                                    'leftDepthWall',
                                    'leftWidthWall',
                                ];
                                break;
                            case 'frontWall':
                                hiddenWalls = [
                                    'leftDepthWall',
                                    'leftWidth2Wall',
                                    'rightWidth2Wall',
                                ];
                                break;
                            case 'rightWall':
                                hiddenWalls = [
                                    'leftWidth2Wall',
                                    'rightWidth2Wall',
                                    'rightDepthWall',
                                ];
                                break;
                            default:
                                hiddenWalls = [
                                    'rightWidthWall',
                                    'rightDepthWall',
                                ];
                                break;
                        }
                    }

                    arrow.visible = !hiddenWalls.includes(
                        arrow.userData.id as string
                    );
                }
            });
        }
    }, [showArrows, hiddenWall, roomType]);

    useEffect(() => {
        // Check if cabinet values have changed by comparing serialized keys
        // This works for all products, not just manual panels
        const currentValuesKey = getCabinetValuesKey(selectedCabinetGroup);
        const valuesChanged = prevCabinetValuesRef.current !== currentValuesKey;
        if (valuesChanged && currentValuesKey) {
            prevCabinetValuesRef.current = currentValuesKey;
        }

        // Early return if no active group - hide everything and exit

        if (!selectedCabinetGroup || !showCabinets) {
            // Hide specific arrow groups when no active cabinet
            arrowGroupRefs.current?.forEach((arrow) => {
                const shouldHide =
                    arrow.userData.id === 'arrowGroupTop' ||
                    arrow.userData.id === 'arrowGroupFront' ||
                    arrow.userData.id === 'arrowGroupLeft' ||
                    arrow.userData.id === 'arrowGroupRight' ||
                    arrow.userData.id === 'arrowGroupBack' ||
                    arrow.userData.id === 'arrowGroupLeft-distance' ||
                    arrow.name === 'cabinetDimensionArrows' ||
                    arrow.name === 'cabinetMovementArrows' ||
                    arrow.name === 'distanceWallMeasurements' ||
                    arrow.name === 'distanceWallMeasurementsDashed';

                if (shouldHide) {
                    arrow.visible = false;
                    arrow.traverse((child) => {
                        child.visible = false;
                    });
                }
            });
            return;
        }

        // i want to see al the arrows in the arrowRefs.current
        arrowRefs.current.forEach((arrow) => {
            arrow.visible = true;
        });

        // Cache frequently used values
        cabinetGroup = selectedCabinetGroup;
        const isLShapePanel = selectedCabinetGroup.userData.isLShapePanel;
        const {
            depth,
            width,
            height: heightGroup,
        } = getGroupWidth(selectedCabinetGroup);

        const shouldShowArrows = showArrows && showCabinets;
        const currentDirection = cabinetGroup.userData
            .currentDirection as string;
        const isActiveGroupId = cabinetGroup.userData.id as string;

        // For manual panels, use thickness instead of height since they lay flat
        const isManualPanel = selectedCabinetGroup.userData.isManualPanel;
        const shouldAddToeKick =
            Boolean(cabinetGroup.userData.isPanelProduct) ||
            Boolean(cabinetGroup.userData.isFillerProduct) ||
            Boolean(cabinetGroup.userData.isRobeProduct) ||
            Boolean(cabinetGroup.userData.isVerminProduct);

        let height: number;
        if (selectedCabinetGroup.userData?.isPlaceholder) {
            // Placeholders have no cabinet_height — use mesh AABB height.
            height = heightGroup;
        } else if (isManualPanel) {
            const templateName = selectedCabinetGroup.userData.templateName;
            const isExteriorPanel = templateName?.includes('Exterior');
            const values = selectedCabinetGroup.userData.values as {
                cabinet_ext_thickness?: number | string;
                cabinet_carc_thickness?: number | string;
            };
            const thickness = isExteriorPanel
                ? Number(values.cabinet_ext_thickness)
                : Number(values.cabinet_carc_thickness);
            height = thickness * scale;
        } else if (isLShapePanel || shouldAddToeKick) {
            height = heightGroup;
        } else if (
            selectedCabinetGroup.userData.values.type_name == 'Drawer Face'
        ) {
            const values = selectedCabinetGroup.userData.values as {
                cabinet_panel_length?: number | string;
            };
            height = Number(values.cabinet_panel_length) * scale;
        } else {
            const values = selectedCabinetGroup.userData.values as {
                cabinet_height?: number | string;
            };
            height = Number(values.cabinet_height) * scale;
        }
        // Calculate room dimensions once
        const roomLength =
            roomType === 'RECTANGULAR'
                ? rectangularDimension.length
                : lShapedDimension.leftWidth;
        const roomWidth =
            roomType === 'RECTANGULAR'
                ? rectangularDimension.width
                : lShapedDimension.rightWidth;
        const dimension =
            roomType === 'RECTANGULAR'
                ? rectangularDimension
                : lShapedDimension;

        // Wall-distance helpers are tied to a cabinet mesh (uuid). Product edit replaces
        // the mesh while userData.id stays the same — drop stale groups so they are recreated.

        arrowGroupRefs.current =
            arrowGroupRefs.current?.filter((arrow) => {
                if (
                    arrow.name !== 'distanceWallMeasurementsDashed' &&
                    arrow.name !== 'distanceWallMeasurements'
                ) {
                    return true;
                }
                const ud = arrow.userData as {
                    id?: string;
                    cabinetTargetUuid?: string;
                    dispose?: () => void;
                };
                if (ud.id !== isActiveGroupId) {
                    return true;
                }
                if (ud.cabinetTargetUuid === cabinetGroup.uuid) {
                    return true;
                }
                try {
                    ud.dispose?.();
                } catch {
                    // ignore
                }
                return false;
            }) || [];

        // Handle all arrow groups in a single loop
        const isHorizontal =
            currentDirection === 'left' || currentDirection === 'right';
        const existingDimensionArrows = new Set<string>();
        const existingMovementArrows = new Set<string>();
        const existingDistanceWallMeasurements = new Set<string>();
        const existingDistanceWallDashedLines = new Set<string>();

        // Hide all cabinetMovementArrows and distanceWallMeasurements from non-active cabinets
        // Visibility for active cabinet will be handled by dragVisibilityHelpers
        arrowGroupRefs.current?.forEach((arrow) => {
            if (
                arrow.name === 'cabinetMovementArrows' ||
                arrow.name === 'distanceWallMeasurements' ||
                arrow.name === 'distanceWallMeasurementsDashed'
            ) {
                const isActiveGroupArrow =
                    arrow.userData?.id === isActiveGroupId;
                if (!isActiveGroupArrow) {
                    // Hide arrows from non-active cabinets
                    arrow.visible = false;
                    arrow.traverse((child) => {
                        child.visible = false;
                    });
                }
            }
        });

        arrowGroupRefs.current?.forEach((arrow) => {
            const arrowId = arrow.userData.id as string;
            const arrowName = arrow.name;
            const isActiveGroupArrow = arrowId === isActiveGroupId;

            // Check if arrow is still attached to scene and not disposed
            const isArrowValid =
                arrow && arrow.parent && !arrow.userData?.isDisposed;

            // Track existing arrows to avoid duplicate creation, but only if they're valid
            if (
                arrowName === 'cabinetDimensionArrows' &&
                isActiveGroupArrow &&
                isArrowValid
            ) {
                existingDimensionArrows.add(arrowId);
            }
            if (
                arrowName === 'cabinetMovementArrows' &&
                isActiveGroupArrow &&
                isArrowValid
            ) {
                existingMovementArrows.add(arrowId);
            }
            if (
                arrowName === 'distanceWallMeasurements' &&
                isActiveGroupArrow &&
                isArrowValid &&
                (((arrow.userData as {cabinetTargetUuid?: string})
                    .cabinetTargetUuid &&
                    (arrow.userData as {cabinetTargetUuid?: string})
                        .cabinetTargetUuid === cabinetGroup.uuid) ||
                    !(arrow.userData as {cabinetTargetUuid?: string})
                        .cabinetTargetUuid)
            ) {
                existingDistanceWallMeasurements.add(arrowId);
            }

            if (
                arrowName === 'distanceWallMeasurementsDashed' &&
                isActiveGroupArrow &&
                isArrowValid &&
                (arrow.userData as {cabinetTargetUuid?: string})
                    .cabinetTargetUuid === cabinetGroup.uuid
            ) {
                existingDistanceWallDashedLines.add(arrowId);
            }
            // Handle different arrow types
            if (arrowName === 'cabinetDimensionArrows') {
                if (!isArrowValid) {
                    // Arrow is invalid, skip it - it will be recreated below
                    return;
                }

                const shouldShow =
                    shouldShowArrows && isActiveGroupArrow && !isDragging;
                arrow.visible = shouldShow;
                arrow.traverse((child) => {
                    child.visible = shouldShow;
                });

                if (shouldShow) {
                    // Re-attach to scene if not attached
                    if (!arrow.parent && cabinetGroup.parent) {
                        cabinetGroup.parent.add(arrow);
                    }

                    syncOverlayTransformToCabinet(arrow, cabinetGroup);

                    if (
                        arrow.userData?.onDrag &&
                        typeof arrow.userData.onDrag === 'function'
                    ) {
                        if (isHorizontal) {
                            (
                                arrow.userData.onDrag as (
                                    w: number,
                                    d: number,
                                    h: number
                                ) => void
                            )(depth, width, height);
                        } else {
                            (
                                arrow.userData.onDrag as (
                                    w: number,
                                    d: number,
                                    h: number
                                ) => void
                            )(width, depth, height);
                        }
                    }
                }
            } else if (arrowName === 'cabinetMovementArrows') {
                if (!isArrowValid) {
                    // Arrow is invalid, skip it - it will be recreated below
                    return;
                }

                if (isActiveGroupArrow) {
                    // Re-attach to scene if not attached
                    if (!arrow.parent && cabinetGroup.parent) {
                        cabinetGroup.parent.add(arrow);
                    }

                    syncOverlayTransformToCabinet(arrow, cabinetGroup);

                    // Visibility is handled by dragVisibilityHelpers
                    // Only update position and call onDrag handlers here
                    if (
                        arrow.userData?.onDrag &&
                        typeof arrow.userData.onDrag === 'function'
                    ) {
                        if (isHorizontal) {
                            (
                                arrow.userData.onDrag as (
                                    w: number,
                                    d: number,
                                    h: number
                                ) => void
                            )(depth, width, height);
                        } else {
                            (
                                arrow.userData.onDrag as (
                                    w: number,
                                    d: number,
                                    h: number
                                ) => void
                            )(width, depth, height);
                        }
                    }
                }
            } else if (arrowName === 'distanceWallMeasurements') {
                if (!isArrowValid) {
                    // Arrow is invalid, skip it - it will be recreated below
                    return;
                }
                const distanceTargetUuid = (
                    arrow.userData as {cabinetTargetUuid?: string}
                ).cabinetTargetUuid;
                if (
                    distanceTargetUuid &&
                    distanceTargetUuid !== cabinetGroup.uuid
                ) {
                    return;
                }
                if (isActiveGroupArrow) {
                    // Re-attach to scene if not attached
                    if (!arrow.parent && cabinetGroup.parent) {
                        cabinetGroup.parent.add(arrow);
                    }

                    syncOverlayTransformToCabinet(arrow, cabinetGroup);

                    // Visibility is handled by dragVisibilityHelpers (same as cabinetMovementArrows)
                    // Only update position and call onDrag handlers here
                    if (
                        arrow.userData?.onDrag &&
                        typeof arrow.userData.onDrag === 'function'
                    ) {
                        if (isHorizontal) {
                            (
                                arrow.userData.onDrag as (
                                    w: number,
                                    d: number,
                                    h: number
                                ) => void
                            )(depth, width, height);
                        } else {
                            (
                                arrow.userData.onDrag as (
                                    w: number,
                                    d: number,
                                    h: number
                                ) => void
                            )(width, depth, height);
                        }
                    }
                }
            } else if (arrowName === 'distanceWallMeasurementsDashed') {
                if (isActiveGroupArrow) {
                    if (!arrow.parent && cabinetGroup.parent) {
                        cabinetGroup.parent.add(arrow);
                    }
                    const dashedFloorY =
                        (arrow.userData as {floorWorldY?: number})
                            .floorWorldY ?? 0;
                    arrow.position.set(
                        cabinetGroup.position.x,
                        dashedFloorY,
                        cabinetGroup.position.z
                    );
                    arrow.rotation.copy(cabinetGroup.rotation);
                    if (
                        arrow.userData?.onDrag &&
                        typeof arrow.userData.onDrag === 'function'
                    ) {
                        (arrow.userData.onDrag as () => void)();
                    }
                    setObjectVisibility(arrow, true);
                }
            } else {
                // Handle wall measurement arrow groups (arrowGroupTop, arrowGroupFront, etc.)
                const wallArrowsVisible = shouldShowArrows && !isDragging;
                if (shouldShowArrows) {
                    // Pre-calculate commonly used values
                    let effectiveDepth2Wall = 0;
                    let effectiveWidth2Wall = 0;

                    if (currentDirection === 'left') {
                        effectiveWidth2Wall = width;
                    } else if (currentDirection === 'back') {
                        effectiveDepth2Wall = -depth;
                        effectiveWidth2Wall = width;
                    } else if (currentDirection === 'right') {
                        effectiveDepth2Wall = -depth;
                    }

                    const ADD_DISTANCE_FROM_CABINET = 25;
                    const HALF_PI = Math.PI / 2;
                    const isRectangular = roomType === 'RECTANGULAR';
                    const offset = isLShapePanel ? 0 : 15;

                    // Pre-calculated spatial values
                    const halfRoomWidth = (roomWidth * scale) / 2;
                    const halfRoomLength = (roomLength * scale) / 2;
                    const halfDimensionHeight = (dimension.height * scale) / 2;
                    // Same origin as a floor cabinet (`position` = near-min
                    // corner). Placeholders remap their mesh into that frame
                    // so height arrows stay on the left face through rotation.
                    const origin = getCabinetLikeWorldOrigin(cabinetGroup);
                    const baseHeight = origin.y + halfDimensionHeight;
                    const heightArrowLocalY = baseHeight - 50;
                    const heightArrowLocalYSide = baseHeight - 70;
                    const baseCabinetX = origin.x;
                    const baseCabinetZ = origin.z;
                    const negCabinetX = -baseCabinetX;
                    const negCabinetZ = -baseCabinetZ;

                    // Utility functions
                    const resetRotation = () => {
                        arrow.rotation.x = 0;
                        arrow.rotation.y = 0;
                        arrow.rotation.z = 0;
                    };

                    const invokeOnDrag = (w: number, d: number, h?: number) => {
                        if (
                            arrow.userData?.onDrag &&
                            typeof arrow.userData.onDrag === 'function'
                        ) {
                            if (h !== undefined) {
                                (
                                    arrow.userData.onDrag as (
                                        w: number,
                                        d: number,
                                        g: THREE.Group,
                                        h: number
                                    ) => void
                                )(w, d, cabinetGroup, h);
                            } else {
                                (
                                    arrow.userData.onDrag as (
                                        w: number,
                                        d: number,
                                        g: THREE.Group
                                    ) => void
                                )(w, d, cabinetGroup);
                            }
                        }
                    };

                    switch (arrowId) {
                        case 'arrowGroupTop':
                            arrow.position.z = isRectangular ? -50 : 0;
                            arrow.position.x = isRectangular
                                ? negCabinetX + halfRoomWidth + 335
                                : baseCabinetX +
                                  (lShapedDimension.rightWidth * scale) / 2;
                            break;
                        case 'arrowGroupBack':
                            resetRotation();
                            arrow.position.z = -50;
                            arrow.position.y = 10;

                            if (isRectangular) {
                                arrow.position.x =
                                    negCabinetX +
                                    halfRoomWidth -
                                    width +
                                    20 +
                                    effectiveWidth2Wall;
                            } else {
                                const baseX = negCabinetX + halfRoomWidth;
                                if (currentDirection === 'left') {
                                    arrow.position.x = baseX + 40;
                                } else if (currentDirection === 'right') {
                                    arrow.position.x =
                                        baseX -
                                        effectiveWidth2Wall / 2 -
                                        width +
                                        47;
                                } else if (currentDirection === 'back') {
                                    arrow.position.x = baseX + 50;
                                } else {
                                    arrow.position.x =
                                        baseX - effectiveWidth2Wall;
                                }
                            }
                            invokeOnDrag(width, depth);
                            break;
                        case 'arrowGroupFront':
                            if (isRectangular) {
                                const isLeftOrRight =
                                    currentDirection === 'left' ||
                                    currentDirection === 'right';
                                if (isLeftOrRight) {
                                    arrow.position.z =
                                        negCabinetZ +
                                        halfRoomLength -
                                        10 +
                                        effectiveDepth2Wall +
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.position.x =
                                        negCabinetX +
                                        halfRoomWidth +
                                        (currentDirection === 'left'
                                            ? width - 10
                                            : 0);
                                    arrow.position.y = baseHeight + height - 40;
                                    arrow.rotation.x = -HALF_PI;
                                    arrow.rotation.y = HALF_PI;
                                    invokeOnDrag(width, depth, height - offset);
                                } else {
                                    resetRotation();
                                    arrow.position.z = -10;
                                    arrow.position.y = 10;
                                    arrow.position.x =
                                        negCabinetX +
                                        halfRoomWidth -
                                        width +
                                        20 +
                                        effectiveWidth2Wall;
                                    invokeOnDrag(width, depth);
                                }
                            } else {
                                const isLeftOrRight =
                                    currentDirection === 'left' ||
                                    currentDirection === 'right';
                                if (isLeftOrRight) {
                                    arrow.position.z =
                                        baseCabinetZ -
                                        halfRoomLength +
                                        (currentDirection === 'left'
                                            ? 20 + effectiveDepth2Wall
                                            : -effectiveDepth2Wall + 20);
                                    arrow.position.x =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        (currentDirection === 'left'
                                            ? width +
                                              ADD_DISTANCE_FROM_CABINET +
                                              25
                                            : 55);
                                    arrow.position.y = baseHeight + height - 40;
                                    arrow.rotation.x = -HALF_PI;
                                    arrow.rotation.y = HALF_PI;
                                    invokeOnDrag(width, depth, height - 15);
                                } else {
                                    resetRotation();
                                    arrow.position.z = -10;
                                    arrow.position.y = 10;
                                    arrow.position.x =
                                        baseCabinetX + halfRoomWidth - 25;
                                    invokeOnDrag(width, depth);
                                }
                            }
                            break;
                        case 'arrowGroupLeft':
                            if (isRectangular) {
                                const isLeftOrRight =
                                    currentDirection === 'left' ||
                                    currentDirection === 'right';
                                if (isLeftOrRight) {
                                    arrow.position.y = heightArrowLocalYSide;
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength -
                                        10 +
                                        effectiveDepth2Wall;
                                    arrow.position.z = 10;
                                    arrow.rotation.z = 0;
                                    invokeOnDrag(width, depth);
                                } else if (currentDirection === 'back') {
                                    arrow.position.y = heightArrowLocalY;
                                    arrow.position.x =
                                        negCabinetZ + halfRoomLength;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        20 -
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.rotation.z = HALF_PI;
                                    invokeOnDrag(width, depth, height);
                                } else {
                                    arrow.position.y = heightArrowLocalY;
                                    arrow.position.x =
                                        negCabinetZ + halfRoomLength - 10;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        80 +
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.rotation.z = HALF_PI;
                                    invokeOnDrag(width, depth, height);
                                }
                            } else {
                                const isLeftOrRight =
                                    currentDirection === 'left' ||
                                    currentDirection === 'right';
                                if (isLeftOrRight) {
                                    arrow.position.y = heightArrowLocalYSide;
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength -
                                        30 +
                                        effectiveDepth2Wall;
                                    arrow.position.z = 10;
                                    arrow.rotation.z = 0;
                                    invokeOnDrag(depth, width);
                                } else if (currentDirection === 'back') {
                                    arrow.position.y = baseHeight - 60;
                                    arrow.position.x =
                                        negCabinetZ + halfRoomLength;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        20 -
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.rotation.z = HALF_PI;
                                    invokeOnDrag(width, depth, height - 15);
                                } else {
                                    arrow.position.y = baseHeight - 65;
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength +
                                        20 -
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        80 +
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.rotation.z = HALF_PI;
                                    invokeOnDrag(width, depth, height - 10);
                                }
                            }
                            break;
                        case 'arrowGroupLeft-distance':
                            arrow.rotation.z = HALF_PI;
                            // Placeholders: mesh AABB bottom vs floor plane.
                            // Cabinets: legacy pivot formula (baseHeight - 50).
                            const heightOffset = getFloorClearanceSceneUnits(
                                cabinetGroup,
                                dimension.height * scale
                            );

                            if (isRectangular) {
                                const isLeftOrRight =
                                    currentDirection === 'left' ||
                                    currentDirection === 'right';
                                if (isLeftOrRight) {
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        (currentDirection === 'left'
                                            ? width + 45
                                            : 55);
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength +
                                        (currentDirection === 'right'
                                            ? effectiveDepth2Wall
                                            : 0) -
                                        35 +
                                        ADD_DISTANCE_FROM_CABINET;
                                    invokeOnDrag(width, depth, heightOffset);
                                } else if (currentDirection === 'back') {
                                    arrow.position.y = 0;
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength -
                                        25 +
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        20 -
                                        ADD_DISTANCE_FROM_CABINET;
                                    invokeOnDrag(width, depth, heightOffset);
                                } else {
                                    arrow.position.y = 0;
                                    arrow.position.x =
                                        negCabinetZ + halfRoomLength - 10;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        80 +
                                        ADD_DISTANCE_FROM_CABINET;
                                    invokeOnDrag(width, depth, heightOffset);
                                }
                            } else {
                                resetRotation();
                                arrow.rotation.z = HALF_PI;

                                const isLeftOrRight =
                                    currentDirection === 'left' ||
                                    currentDirection === 'right';
                                if (isLeftOrRight) {
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth +
                                        (currentDirection === 'left'
                                            ? -width - 50
                                            : 25);
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength +
                                        effectiveDepth2Wall -
                                        (currentDirection === 'left'
                                            ? 35 - ADD_DISTANCE_FROM_CABINET
                                            : 20);
                                    if (currentDirection === 'right')
                                        arrow.rotation.y = -Math.PI;
                                } else if (currentDirection === 'back') {
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        20 -
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.position.x =
                                        negCabinetZ + halfRoomLength;
                                } else {
                                    arrow.position.y = 0;
                                    arrow.position.x =
                                        negCabinetZ +
                                        halfRoomLength +
                                        20 -
                                        ADD_DISTANCE_FROM_CABINET;
                                    arrow.position.z =
                                        baseCabinetX +
                                        halfRoomWidth -
                                        80 +
                                        ADD_DISTANCE_FROM_CABINET;
                                }
                                invokeOnDrag(width, depth, heightOffset);
                            }
                            break;
                        case 'arrowGroupRight':
                            arrow.position.z = isRectangular ? -45 : 0;
                            arrow.position.x = isRectangular
                                ? negCabinetZ +
                                  halfRoomLength +
                                  20 +
                                  effectiveDepth2Wall
                                : baseCabinetZ +
                                  halfRoomLength -
                                  effectiveDepth2Wall -
                                  depth +
                                  22;
                            invokeOnDrag(width, depth);
                            break;
                    }
                }
                setObjectVisibility(arrow, wallArrowsVisible);
            }
            if (arrow.userData.setGuidelinesVisible && !isDragging) {
                // Only show guidelines for the active group
                if (
                    arrow.userData?.onDrag &&
                    typeof arrow.userData.onDrag === 'function'
                ) {
                    (arrow.userData.onDrag as (visible: boolean) => void)(true);
                }
            }
        });

        if (!existingMovementArrows.has(isActiveGroupId)) {
            const cabinetMovementArrowGroup = addCabinetMovementArrows(
                cabinetGroup,
                handleArrowsAndGroup
            );
            if (cabinetMovementArrowGroup) {
                arrowGroupRefs.current.push(cabinetMovementArrowGroup);
                // Initial visibility is false, will be handled by dragVisibilityHelpers
                cabinetMovementArrowGroup.visible = false;
            }
        }

        // Create distance wall measurements (same as movement arrows - always create, visibility controlled separately)
        if (!existingDistanceWallMeasurements.has(isActiveGroupId)) {
            const cabinetExistingDistanceWallMeasurements =
                addDistanceWallMeasurements(
                    cabinetGroup,
                    handleArrowsAndGroup,
                    roomType,
                    rectangularDimension,
                    lShapedDimension,
                    scale,
                    cabinetGroupListRef.current
                );
            if (cabinetExistingDistanceWallMeasurements) {
                arrowGroupRefs.current.push(
                    cabinetExistingDistanceWallMeasurements
                );

                // Initial visibility is false, will be handled by dragVisibilityHelpers (same as cabinetMovementArrows)
                cabinetExistingDistanceWallMeasurements.visible = false;

                if (cabinetExistingDistanceWallMeasurements.userData?.onDrag) {
                    const {width, depth, height} = getGroupWidth(cabinetGroup);
                    (
                        cabinetExistingDistanceWallMeasurements.userData as {
                            onDrag: (
                                width: number,
                                depth: number,
                                height: number
                            ) => void;
                        }
                    ).onDrag(width, depth, height);
                }
            }
        }
        // Floor dashed lines: wall gaps from calculateCabinetWallDistances
        if (!existingDistanceWallDashedLines.has(isActiveGroupId)) {
            const dashedLineGroup = addDashedLines(
                cabinetGroup,
                (lines, lineGroup) =>
                    handleArrowsAndGroup(
                        lines as (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                            | THREE.Mesh
                        )[],
                        lineGroup
                    ),
                roomType,
                rectangularDimension,
                lShapedDimension,
                scale
            );
            if (dashedLineGroup) {
                arrowGroupRefs.current.push(dashedLineGroup);
                dashedLineGroup.visible = false;
                (dashedLineGroup.userData as {onDrag?: () => void}).onDrag?.();
            }
        }
    }, [
        isDragging,
        showArrows,
        cabinetGroup,
        rectangularDimension,
        lShapedDimension,
        roomType,
        showCabinets,
        selectedCabinetGroup,
        rotating,
        wallRefs,
        getCabinetValuesKey,
        selectedCabinetGroup,
    ]);

    useEffect(() => {
        if (isTopView) {
            topViewArrowRefs.current.forEach((arrow) => {
                arrow.visible = true;
            });
        } else {
            topViewArrowRefs.current.forEach((arrow) => {
                arrow.visible = false;
            });
        }
    }, [isTopView]);

    useEffect(() => {
        if (leftLeveledStaticArrowRef.current) {
            if (isEditHeightView) {
                leftLeveledStaticArrowRef.current.visible = true;
            } else {
                leftLeveledStaticArrowRef.current.visible = false;
            }
        }
    }, [isEditHeightView]);

    useEffect(() => {
        isWireframeModeRef.current = isWireframeMode;

        if (floorRef.current) {
            (
                floorRef.current.userData as {
                    onToggleWireframe: (isWireframe: boolean) => void;
                }
            ).onToggleWireframe(isWireframeMode);
        }
    }, [isWireframeMode]);

    // Track isMovementMode to force visibility updates when it changes
    // This ensures arrows show/hide immediately when isMovementMode is toggled
    const prevIsMovementModeRef = useRef<boolean | undefined>(undefined);
    // Use a ref to track isMovementMode so we can detect changes even when selectedCabinetGroup reference doesn't change
    const isMovementModeRef = useRef<boolean>(false);

    useEffect(() => {
        if (!selectedCabinetGroup) {
            prevIsMovementModeRef.current = undefined;
            isMovementModeRef.current = false;
            arrowGroupRefs.current?.forEach((arrow) => {
                if (
                    arrow.name === 'cabinetMovementArrows' ||
                    arrow.name === 'distanceWallMeasurements' ||
                    arrow.name === 'distanceWallMeasurementsDashed'
                ) {
                    setObjectVisibility(arrow, false);
                }
            });
            return;
        }

        const currentIsMovementMode = Boolean(
            selectedCabinetGroup.userData.isMovementMode
        );
        isMovementModeRef.current = currentIsMovementMode;

        // Always update visibility to ensure it's correct when isMovementMode changes
        // This handles the case where isMovementMode changes but selectedCabinetGroup reference doesn't
        // Always update visibility (not just on change) to ensure it's correct
        arrowGroupRefs.current?.forEach((arrow) => {
            if (
                (arrow.name === 'distanceWallMeasurements' ||
                    arrow.name === 'distanceWallMeasurementsDashed' ||
                    arrow.name === 'cabinetMovementArrows') &&
                arrow.userData?.id === selectedCabinetGroup.userData.id
            ) {
                const arrowType = getArrowType(arrow);
                const shouldShow = calculateArrowVisibility(
                    arrowType,
                    arrow,
                    isDragging,
                    showCabinets,
                    showArrows,
                    selectedCabinetGroup
                );
                setObjectVisibility(arrow, shouldShow);
            }
        });

        prevIsMovementModeRef.current = currentIsMovementMode;
    }, [
        selectedCabinetGroup,
        isDragging,
        showCabinets,
        showArrows,
        rotating,
        hiddenWall,
    ]);

    // Hide and Show Arrows and Lines when dragging
    // This also handles movement arrows visibility when isMovementMode changes
    useEffect(() => {
        // Hide all movement arrows and distance measurements when no cabinet is selected
        if (!selectedCabinetGroup) {
            arrowGroupRefs.current?.forEach((arrow) => {
                if (
                    arrow.name === 'cabinetMovementArrows' ||
                    arrow.name === 'distanceWallMeasurements' ||
                    arrow.name === 'distanceWallMeasurementsDashed'
                ) {
                    setObjectVisibility(arrow, false);
                }
            });
            return;
        }

        // Dispose Reposition Arrows when dragging starts
        disposeMovementArrowsOnDrag(
            isDragging,
            selectedCabinetGroup,
            arrowGroupRefs
        );

        // Update arrow visibility based on dragging state and isMovementMode
        // This ensures movement arrows are hidden when isMovementMode is off
        // calculateArrowVisibility checks isMovementMode, so visibility will be correct
        arrowGroupRefs.current?.forEach((arrow) => {
            const arrowType = getArrowType(arrow);
            const shouldShow = calculateArrowVisibility(
                arrowType,
                arrow,
                isDragging,
                showCabinets,
                showArrows,
                selectedCabinetGroup
            );
            setObjectVisibility(arrow, shouldShow);
        });
    }, [isDragging, showCabinets, showArrows, selectedCabinetGroup, rotating]);

    // Setup WallMeasurements and DistanceWallMeasurements when not dragging
    useEffect(() => {
        // Only setup measurements when not dragging
        // When dragging stops, this will re-run and recreate measurements
        if (!isRoomPlannerReportGenerating) {
            printMeasurementsBuiltRef.current = false;
        }
        if (showArrows && showCabinets && !isDragging) {
            // setupMeasurements can work even without selectedCabinetGroup (for wall measurements)
            // But it requires a cabinetGroup parameter, so we pass selectedCabinetGroup or use first cabinet
            // Always use a cabinet group if available to ensure measurements are recreated when dragging stops
            const cabinetGroupForMeasurements = getItemsForWallMeasurements(
                cabinetGroupListRef.current,
                placeholderListRef?.current
            );

            if (isRoomPlannerReportGenerating) {
                // Build every print view's measurement groups once, tagged
                // with their view; the snapshot loop only toggles visibility
                // per stage (including each elevation's excluded cabinets),
                // so stages don't rebuild and drift from each other.
                if (
                    !printMeasurementsBuiltRef.current &&
                    cabinetGroupForMeasurements
                ) {
                    PRINT_MEASUREMENT_VIEWS.forEach((printView, index) => {
                        setupMeasurements(
                            wallRefs,
                            cabinetGroupForMeasurements,
                            handleArrowsAndGroup,
                            getGroupWidth,
                            roomType,
                            rectangularDimension,
                            lShapedDimension,
                            scale,
                            showCabinets,
                            showArrows,
                            cabinetGroup,
                            getPrintLabelFontSize(printView),
                            true,
                            printView,
                            hiddenWall,
                            index > 0
                        );
                    });

                    // Hide all tagged groups until the snapshot loop shows
                    // the active stage's groups.
                    wallRefs.current?.forEach((wall) => {
                        wall?.traverse((child) => {
                            if (child.userData?.printView) {
                                child.visible = false;
                            }
                        });
                    });

                    printMeasurementsBuiltRef.current = true;
                }
            } else if (cabinetGroupForMeasurements) {
                setupMeasurements(
                    wallRefs,
                    cabinetGroupForMeasurements,
                    handleArrowsAndGroup,
                    getGroupWidth,
                    roomType,
                    rectangularDimension,
                    lShapedDimension,
                    scale,
                    showCabinets,
                    showArrows,
                    cabinetGroup,
                    defaultLabelFontSize,
                    isRoomPlannerReportGenerating,
                    currentPreviewPosition,
                    hiddenWall
                );
            }
        } else if ((!showArrows || isDragging) && wallRefs.current) {
            printMeasurementsBuiltRef.current = false;
            // Remove measurements from all walls when showArrows is false or when dragging
            wallRefs.current.forEach((wall) => {
                if (wall) {
                    removeExistingMeasurements(wall);
                }
            });
        }

        // Cabinets and placeholders both take part in item-to-item distance
        // detection, so a fridge/oven is measured like any other neighbour.
        const itemsForDistance = getItemsForWallMeasurements(
            cabinetGroupListRef.current,
            placeholderListRef?.current
        ) as CabinetMesh[];

        // Cabinet gap lines follow the selection. The Dimensions toggle does not hide them.
        if (
            showCabinets &&
            CameraSetup.scene &&
            selectedCabinetGroup &&
            !isRoomPreview
        ) {
            distanceLinesGroupRef.current = setupDistanceLines(
                CameraSetup.scene,
                selectedCabinetGroup,
                itemsForDistance,
                distanceLinesGroupRef.current
            );
        } else if (distanceLinesGroupRef.current) {
            disposeDistanceLines(distanceLinesGroupRef.current);
        }

        // This ensures buttons move with the cabinet even when ruler is off
        // Hide side buttons when dragging or in room preview
        if (showCabinets && !isDragging && !isRoomPreview) {
            if (
                CameraSetup.scene &&
                CameraSetup.camera &&
                selectedCabinetGroup
            ) {
                const sideButtonGroups = setupSideButtons(
                    CameraSetup.scene,
                    selectedCabinetGroup,
                    itemsForDistance,
                    CameraSetup.camera,
                    sideButtonGroupsRef.current,
                    isMobileDevice
                );
                sideButtonGroupsRef.current = sideButtonGroups;
            } else if (!selectedCabinetGroup) {
                hideSideButtons(sideButtonGroupsRef.current);
            }
        } else {
            hideSideButtons(sideButtonGroupsRef.current);
        }

        // Floor-distance arrows are shared per wall. Re-measure one frame
        // later so a base placeholder's AABB is seated before we paint the
        // gray label (otherwise it can keep a previous cabinet's 1500mm).
        if (
            selectedCabinetGroup?.userData?.isPlaceholder &&
            !isDragging &&
            showArrows
        ) {
            const mesh = selectedCabinetGroup;
            requestAnimationFrame(() => {
                const scene = mesh.parent;
                if (!isScene(scene)) return;
                syncOverlaysOnCabinetMove(mesh, scene, true);
            });
        }
    }, [
        scale,
        wallRefs,
        roomType,
        rectangularDimension,
        lShapedDimension,
        handleArrowsAndGroup,
        showArrows,
        selectedCabinetGroup,
        hiddenWall,
        isRoomPlannerReportGenerating,
        showCabinets,
        isDragging,
        isRoomPreview,
        cabinetGroupListRef,
        placeholderListRef,
        placeholderIdsKey,
        isMobileDevice,
    ]);

    // Effect to update measurement visibility when walls are hidden/shown
    useEffect(() => {
        // During report generation the snapshot loop owns measurement
        // visibility (groups are tagged per print view) - don't fight it.
        if (isRoomPlannerReportGenerating) {
            return;
        }
        if (wallRefs.current && hiddenWall) {
            const hiddenWalls = hiddenWall
                .split(',')
                .map((wall) => wall.trim());

            // Don't hide measurements in TOP views
            const shouldKeepAllMeasurementsVisible =
                currentPreviewPosition === 'TOP' ||
                currentPreviewPosition === 'TOP_WITHOUT_EDITING';

            wallRefs.current.forEach((wall) => {
                const isWallHidden = hiddenWalls.includes(
                    wall.userData.id as string
                );

                // Update measurement groups visibility
                wall.traverse((child) => {
                    if (
                        child.userData.type === 'multipleMeasurements' ||
                        child.userData.type === 'emptySpaceMeasurements'
                    ) {
                        // Always show measurements when printing or in TOP views
                        child.visible = shouldKeepAllMeasurementsVisible
                            ? true
                            : !isWallHidden;
                    }
                });
            });
        }
    }, [hiddenWall, isRoomPlannerReportGenerating, currentPreviewPosition]);

    useEffect(() => {
        if (floorRef.current) {
            if (isViewingAtBottom) {
                floorRef.current.visible = false;
            } else {
                floorRef.current.visible = true;
            }
        }
    }, [isViewingAtBottom]);

    const prevFormRectDimension = useRef<RectangularDimension | null>(null);
    const prevFormLShapeDimension = useRef<LShapedDimension | null>(null);
    const oldCabinets = useSelector(getOldCabinetsPosition);
    const oldPositionsRef = useRef<
        Map<string | number, {x: number; z: number}>
    >(new Map());

    // Effect to trigger cabinet repositioning when room dimensions change
    useEffect(() => {
        if (
            isEditingRoomDimension &&
            wallRefs.current &&
            wallRefs.current.length > 0 &&
            cabinetGroupListRef.current &&
            cabinetGroupListRef.current.length > 0
        ) {
            // Check if dimensions actually changed
            const lengthChanged =
                prevFormRectDimension.current?.length !==
                rectangularDimension.length;
            const widthChanged =
                prevFormRectDimension.current?.width !==
                rectangularDimension.width;
            const heightChanged =
                prevFormRectDimension.current?.height !==
                rectangularDimension.height;

            const rectChanged =
                prevRectangularDimension.current.width !==
                    rectangularDimension.width ||
                prevRectangularDimension.current.length !==
                    rectangularDimension.length ||
                prevRectangularDimension.current.height !==
                    rectangularDimension.height;

            const leftWidthChanged =
                prevFormLShapeDimension.current?.leftWidth !==
                lShapedDimension.leftWidth;
            const rightWidthChanged =
                prevFormLShapeDimension.current?.rightWidth !==
                lShapedDimension.rightWidth;

            const lShapedChanged =
                prevLShapedDimension.current.leftWidth !==
                    lShapedDimension.leftWidth ||
                prevLShapedDimension.current.rightWidth !==
                    lShapedDimension.rightWidth ||
                prevLShapedDimension.current.leftDepth !==
                    lShapedDimension.leftDepth ||
                prevLShapedDimension.current.rightDepth !==
                    lShapedDimension.rightDepth ||
                prevLShapedDimension.current.height !== lShapedDimension.height;

            const lShapedHeightChanged =
                prevLShapedDimension.current.height !== lShapedDimension.height;

            if (rectChanged || lShapedChanged) {
                // Add a small delay to ensure walls are fully updated before repositioning
                const timeoutId = setTimeout(() => {
                    cabinetReposition(
                        wallRefs,
                        cabinetGroupListRef.current as CabinetMesh[],
                        prevRectangularDimension.current,
                        prevLShapedDimension.current,
                        lengthChanged,
                        widthChanged,
                        leftWidthChanged,
                        rightWidthChanged,
                        rectChanged,
                        lShapedChanged,
                        oldPositionsRef,
                        oldCabinets,
                        heightChanged || lShapedHeightChanged,
                        rectangularDimension,
                        lShapedDimension
                    );
                    hasTriggeredRepositioning.current = true;
                }, 100);
                prevFormRectDimension.current = rectangularDimension;
                prevFormLShapeDimension.current = lShapedDimension;
                return () => clearTimeout(timeoutId);
            } else {
                cabinetGroupListRef.current.forEach((product) => {
                    resetCabinetPosition(product as CabinetMesh, oldCabinets);
                });
            }
        }

        // Update previous dimensions for next comparison
        prevRectangularDimension.current = rectangularDimension;
        prevLShapedDimension.current = lShapedDimension;
        repositionCabinetsOutsideRoom(
            cabinetGroupListRef.current as CabinetMesh[]
        );
        oldPositionsRef.current.clear();
    }, [rectangularDimension, lShapedDimension, isEditingRoomDimension]);

    return {
        createLShapedWalls,
        createLShapedFloor,
        createRectangularFloor,
        createRectangularWalls,
        wallRefs,
        isWireframeModeRef,
        sideButtonGroupsRef,
    };
};

export default useRoomLayout;
